import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatViews, getTypeName } from "@/utils/video";

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    if (paused || videos.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % videos.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [paused, videos.length]);

  if (videos.length === 0) return null;

  const goTo = (index: number) => {
    setActive((index + videos.length) % videos.length);
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      goTo(delta > 0 ? active - 1 : active + 1);
    }
    touchStartX.current = null;
  };

  const current = videos[active];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-900"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative h-[420px] w-full md:h-[560px]">
        {videos.map((video, index) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={video.cover}
              alt={`${video.title}焦点推荐`}
              title={`${video.title} - 污视频热门推荐`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>

        <div className="absolute inset-0">
          <div className="flex h-full w-full items-center px-4 md:px-8">
            <div className="max-w-2xl animate-fade-up">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
                <i className="ri-fire-fill"></i>
                本周焦点推荐
              </span>
              <h2 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-5xl">
                {current.title}
              </h2>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-primary-100">
                <span className="flex items-center gap-1 font-semibold text-accent-300">
                  <i className="ri-star-fill"></i>
                  {current.rating.toFixed(1)}
                </span>
                <span>{getTypeName(current.type)}</span>
                <span>{current.region}</span>
                <span>{current.year}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-fire-line"></i>
                  {formatViews(current.views)} 次浏览
                </span>
              </div>
              <p className="mt-4 line-clamp-2 text-sm leading-relaxed text-primary-100 md:text-base">
                {current.description}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${current.id}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-information-line"></i>
                  查看详情
                </Link>
                <Link
                  to={`/category/${current.type}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
                >
                  更多{getTypeName(current.type)}
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一张"
          onClick={() => goTo(active - 1)}
          className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:flex cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-2xl"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => goTo(active + 1)}
          className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:flex cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-2xl"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {videos.map((video, index) => (
            <button
              key={video.id}
              type="button"
              aria-label={`切换到第${index + 1}张`}
              onClick={() => goTo(index)}
              className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                index === active ? "w-8 bg-accent-500" : "w-3 bg-background-50/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}